import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient } from '../../services/apiClient';

const CacheSettingsContext = createContext();

export const useCacheSettings = () => {
  const context = useContext(CacheSettingsContext);
  if (!context) {
    throw new Error('useCacheSettings must be used within a CacheSettingsProvider');
  }
  return context;
};

export const CacheSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    ttl_minutes: 60,
    persist_indefinitely: true,
    cache_enabled: true,
    loading: true,
    error: null
  });

  const fetchSettings = async () => {
    try {
      setSettings(prev => ({ ...prev, loading: true, error: null }));
      const response = await apiClient.get('/cache/settings');
      const data = response.data;
      setSettings(prev => ({
        ...prev,
        ttl_minutes: data.ttl_minutes,
        persist_indefinitely: data.persist_indefinitely,
        cache_enabled: data.cache_enabled ?? true,
        loading: false
      }));
    } catch (error) {
      console.error('Failed to fetch cache settings:', error);
      setSettings(prev => ({
        ...prev,
        loading: false,
        error: error.message,
        persist_indefinitely: true,
        cache_enabled: true
      }));
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const response = await apiClient.put('/cache/settings', newSettings);
      const data = response.data;
      setSettings(prev => ({
        ...prev,
        ttl_minutes: data.ttl_minutes,
        persist_indefinitely: data.persist_indefinitely,
        cache_enabled: data.cache_enabled ?? true
      }));
      return data;
    } catch (error) {
      console.error('Failed to update cache settings:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const value = {
    settings,
    updateSettings,
    refreshSettings: fetchSettings,
    isPersistentCacheEnabled: settings.persist_indefinitely,
    isCacheEnabled: settings.cache_enabled,
    isLoading: settings.loading,
    error: settings.error
  };

  return (
    <CacheSettingsContext.Provider value={value}>
      {children}
    </CacheSettingsContext.Provider>
  );
};

export default CacheSettingsContext;